import { __ } from "@wordpress/i18n";
import { Icon, TextControl } from "@wordpress/components";
import { Button } from "../../components/button";
import { Section } from "../../components/section";
import { DEFAULTS, POST_STATUSES, ENUM, IS_PUBLIC_SITE, IS_PRO, LIMITS } from "../../utils/constants";
import { DocOutlineType, useDoc } from "./context";
import { getLanguageLabel, GPT_LANGUAGES } from "../../utils/language";
import { Badge } from "../../components/badge";
import { TONES } from "../../utils/tones";
import { css } from "@/utils/css";
import { Tooltip } from "@/components/tooltip";
import {
    __experimentalToggleGroupControl as ToggleGroupControl,
    __experimentalToggleGroupControlOption as ToggleGroupControlOption,
} from "@wordpress/components";
import { Select } from "@/components/select";
import { TextInput } from "@/components/text-input";
import { NumberInput } from "@/components/number-input";
import { TextareaInput } from "@/components/textarea-input";
import { FileInput } from "@/components/file-input";
import { api } from "@/config/api-fetch";
import { FormBox } from "@/components/form-box";
import { pdf, Document, Page, Text } from "@react-pdf/renderer";
import { useProModal } from "@/components/pro-alert-box";

const acceptedFiles = [
    {
        value: "txt",
        label: __("Text", "antimanual"),
    },
    {
        value: "html",
        label: __("HTML", "antimanual"),
    },
    {
        value: "md",
        label: __("Markdown", "antimanual"),
    },
    {
        value: "csv",
        label: __("CSV", "antimanual"),
    },
    {
        value: "json",
        label: __("JSON", "antimanual"),
    },
    {
        value: "pdf",
        label: __("PDF", "antimanual"),
    },
];

export function PromptArea() {
    const { loading, error, preferences, setError, setLoading, setOutline, setPreferences } = useDoc();
    const { showProAlert } = useProModal();

    const handleFileChange = async (files?: File[]) => {
        if (!files || !files.length) return;

        const newFiles: File[] = [];

        for (const file of files) {
            if (file.type === "application/pdf") {
                newFiles.push(file);
                continue;
            }

            const text = await file.text();

            const blob = await pdf(
                <Document>
                    <Page style={{ padding: 20, fontFamily: "Courier", fontSize: 10 }}>
                        <Text>{text}</Text>
                    </Page>
                </Document>
            ).toBlob();

            newFiles.push(new File([blob], file.name + ".pdf", { type: "application/pdf" }));
        }

        setPreferences({ files: newFiles });
    };

    const handleSubmit = async (e: React.MouseEvent) => {
        e.preventDefault();

        setLoading(true);
        setError("");

        try {
            const payload: Record<string, any> = {
                subject: preferences?.subject || "",
                language: getLanguageLabel(preferences?.language),
                slug_lang: getLanguageLabel(preferences?.slugLang),
                slug_len: preferences?.slugLengthAuto ? ENUM.AUTO : preferences?.slugLength,
                lessons_count: preferences?.lessonsCountAuto ? ENUM.AUTO : preferences?.lessonsCount,
                sub_lessons_count: preferences?.lessonsCountAuto ? ENUM.AUTO : preferences?.subLessonsCount,
                tone: preferences?.modifiedTone || TONES.find(({ tone }) => tone === preferences?.tone)?.description,
            };

            const formData = new FormData();

            preferences?.files?.forEach((file, index) => {
                formData.append(`files[${index}]`, file);
            });

            for (const key in payload) {
                let value = payload[key];

                if (typeof value === "object") value = JSON.stringify(value);

                formData.append(key, value);
            }

            const outline = await api.post<DocOutlineType>("/generate-doc-outline", formData);

            if (!IS_PRO && outline?.lessons) {
                const lessonsCount = outline.lessons.length;
                const maxSubLessons = Math.max(...outline.lessons.map(lesson => lesson.sub_lessons?.length || 0));

                if (lessonsCount > LIMITS.DOCS_LESSONS_COUNT) {
                    showProAlert(
                        __("Limit Exceeded", "antimanual"),
                        __("Free users can generate a maximum of " + LIMITS.DOCS_LESSONS_COUNT + " lessons. Upgrade to Pro for unlimited lessons.", "antimanual")
                    );
                    setLoading(false);
                    return;
                }

                if (maxSubLessons > LIMITS.DOCS_SUB_LESSONS_COUNT) {
                    showProAlert(
                        __("Limit Exceeded", "antimanual"),
                        __("Free users can generate a maximum of " + LIMITS.DOCS_SUB_LESSONS_COUNT + " sub-lessons per lesson. Upgrade to Pro for unlimited sub-lessons.", "antimanual")
                    );
                    setLoading(false);
                    return;
                }
            }

            setOutline(outline);
        } catch (err: any) {
            setError(err.message || __("An error occurred.", "antimanual"));
        }

        setLoading(false);
    };

    return (
        <Section className="atml-prompt-area">
            <h2 className={css("text-center mb-10")}>{__("Generate Documentation With Antimanual", "antimanual")}</h2>

            <div className={css("grid grid-cols-6 gap-4 mb-5")}>
                <div className={css("col-span-3")}>
                    <Select
                        label={__("Content Language", "antimanual")}
                        help={__("Select the language for the documentation.", "antimanual")}
                        value={(preferences?.language as any) || DEFAULTS.LANGUAGE}
                        onChange={(language) => setPreferences({ language, slugLang: language })}
                        options={GPT_LANGUAGES}
                    />
                </div>
                <div className={css("col-span-3")}>
                    <Select
                        label={__("Post Status", "antimanual")}
                        help={__("Select the status for the post.", "antimanual")}
                        value={(preferences?.status as any) || DEFAULTS.POST_STATUS}
                        onChange={(status) => setPreferences({ status })}
                        options={POST_STATUSES}
                    />
                </div>
            </div>
            <div className={css("grid grid-cols-6 gap-4 mb-5")}>
                <div className={css("col-span-3")}>
                    <Select
                        label={__("Slug Language", "antimanual")}
                        help={__("Select the language for the slugs.", "antimanual")}
                        value={(preferences?.slugLang as any) || DEFAULTS.LANGUAGE}
                        onChange={(slugLang) => setPreferences({ slugLang })}
                        options={GPT_LANGUAGES}
                    />
                </div>
            </div>
            <div className={css("grid grid-cols-6 gap-4 mb-5")}>
                <div className={css("col-span-2")}>
                    <ToggleGroupControl
                        label={__("Slug Length", "antimanual")}
                        value={preferences?.slugLengthAuto ? "auto" : "custom"}
                        onClick={() => {
                            if (!IS_PRO) {
                                showProAlert(
                                    __("Premium Feature", "antimanual"),
                                    __("Custom Slug Length is a premium feature. Upgrade to set custom slug length.", "antimanual")
                                );
                            }
                        }}
                        onChange={(option) => setPreferences({ slugLengthAuto: option === "auto" })}
                        help={__("How many words each slug should have?", "antimanual")}
                    >
                        <ToggleGroupControlOption value="auto" label={__("Auto", "antimanual")} />
                        <ToggleGroupControlOption value="custom" disabled={!IS_PRO} label={__("Custom", "antimanual")} />
                    </ToggleGroupControl>
                </div>
                {!preferences?.slugLengthAuto && (
                    <div className={css("col-span-2")}>
                        <NumberInput
                            label={__("Slug Length (Custom)", "antimanual")}
                            help={__("Specify the length of the slugs in words. (Max 20)", "antimanual")}
                            value={preferences?.slugLength || DEFAULTS.SLUG_LEN}
                            onChange={(e) => setPreferences({ slugLength: e })}
                        />
                    </div>
                )}
            </div>
            <div className={css("grid grid-cols-6 gap-4 mb-5")}>
                <div className={css("col-span-2")}>
                    <ToggleGroupControl
                        label={__("Lessons/Sub-lessons Count", "antimanual")}
                        value={preferences?.lessonsCountAuto ? "auto" : "custom"}
                        onClick={() => {
                            if (!IS_PRO) {
                                showProAlert(
                                    __("Premium Feature", "antimanual"),
                                    __(
                                        "Custom Lessons/Sub-lessons Count is a premium feature. Upgrade to set custom lessons/sub-lessons count.",
                                        "antimanual"
                                    )
                                );
                            }
                        }}
                        onChange={(option) => setPreferences({ lessonsCountAuto: option === "auto" })}
                        help={__("How many lessons/sub-lessons should be generated?", "antimanual")}
                    >
                        <ToggleGroupControlOption value="auto" label={__("Auto", "antimanual")} />
                        <ToggleGroupControlOption value="custom" disabled={!IS_PRO} label={__("Custom", "antimanual")} />
                    </ToggleGroupControl>
                </div>
                {!preferences?.lessonsCountAuto && (
                    <>
                        <div className={css("col-span-2")}>
                            <NumberInput
                                label={__("Lessons Count", "antimanual")}
                                help={__("Specify how many lessons should be generated. (Max 20)", "antimanual")}
                                value={preferences?.lessonsCount || DEFAULTS.LESSONS_COUNT}
                                onChange={(value) =>
                                    setPreferences({
                                        lessonsCount: value,
                                    })
                                }
                            />
                        </div>
                        <div className={css("col-span-2")}>
                            <NumberInput
                                label={__("Sub-lessons Count", "antimanual")}
                                help={__("Specify how many sub-lessons should be generated for each lesson. (Max 20)", "antimanual")}
                                value={preferences?.subLessonsCount || DEFAULTS.SUB_LESSONS_COUNT}
                                onChange={(value) =>
                                    setPreferences({
                                        subLessonsCount: value,
                                    })
                                }
                            />
                        </div>
                    </>
                )}
            </div>
            <TextControl className={css("hidden")} value={""} onChange={() => {}} label={__("Tone", "antimanual")} />
            <FormBox label={__("Tone", "antimanual")}>
                <div className={css("flex justify-start flex-wrap gap-2 mt-5 mb-5")}>
                    {TONES.map(({ tone, label }) => {
                        const isSelected = (preferences?.tone || DEFAULTS.TONE) === tone;

                        return (
                            <Badge
                                onClick={() => {
                                    if (!isSelected) {
                                        setPreferences({ tone, modifiedTone: "" });
                                    }
                                }}
                                isActive={isSelected}
                            >
                                {label}
                                {isSelected && (
                                    <Tooltip
                                        key={tone}
                                        title={__("Modify Tone", "antimanual")}
                                        render={(props) => {
                                            return (
                                                <span
                                                    className={css("pl-1")}
                                                    onClick={() => {
                                                        if (!IS_PRO) {
                                                            showProAlert(
                                                                __("Premium Feature", "antimanual"),
                                                                __(
                                                                    "Tone modification is a premium feature. Upgrade to modify the selected tone as you want.",
                                                                    "antimanual"
                                                                )
                                                            );
                                                            return;
                                                        }

                                                        if (!preferences?.modifiedTone) {
                                                            setPreferences({
                                                                modifiedTone: TONES.find(({ tone: t }) => t === tone)?.description,
                                                            });
                                                        }
                                                    }}
                                                    {...props}
                                                >
                                                    <Icon className={css("badge-icon")} icon={"edit"} />
                                                </span>
                                            );
                                        }}
                                    ></Tooltip>
                                )}
                            </Badge>
                        );
                    })}
                </div>
            </FormBox>
            <TextareaInput
                className={css(!preferences?.modifiedTone ? "hidden" : "block")}
                help={preferences?.modifiedTone && __("Describe the tone you want the documentation to have.", "antimanual")}
                placeholder={__(
                    "Adopt a tone like a course instructor or tutor. Explain concepts step by step, and guide the user as if teaching a class.",
                    "antimanual"
                )}
                value={preferences?.modifiedTone || TONES.find(({ tone }) => tone === preferences?.tone)?.description || ""}
                onChange={(modifiedTone) => setPreferences({ modifiedTone })}
            />
            <TextInput
                label={__("Subject", "antimanual")}
                help={__("Write the subject of the documentation you want to generate.", "antimanual")}
                placeholder={__("JavaScript Basics: Understanding Variables, Functions, and Control Structures", "antimanual")}
                value={preferences?.subject || ""}
                onChange={(subject) => setPreferences({ subject })}
                className={css("mb-5")}
            />

            <FileInput
                className={css("block my-5")}
                multiple
                showFiles
                handleClick={(e) => {
                    if (!IS_PRO) {
                        e.preventDefault();

                        showProAlert(
                            __("Premium Feature", "antimanual"),
                            __("Knowledge files upload is a premium feature. Upgrade to upload knowledge files.", "antimanual")
                        );
                    }
                }}
                accept={acceptedFiles.map((file) => `.${file.value}`).join(",")}
                label={__("Upload knowledge files", "antimanual")}
                help={
                    <>
                        <span>{__("AI will use the information in these files to generate docs.", "antimanual")}</span>
                        <br />
                        <span>{__("You can upload", "antimanual")}</span>{" "}
                        {acceptedFiles.map((file, index) => {
                            const isLast = index === acceptedFiles.length - 1;
                            const isFirst = index === 0;

                            return (
                                <span key={file.value}>
                                    {isFirst ? "" : isLast ? `, ${__("or", "antimanual")} ` : ", "}
                                    {file.label}
                                </span>
                            );
                        })}{" "}
                        {__("files.", "antimanual")}
                        {!IS_PUBLIC_SITE && (
                            <span className={css("text-olive font-semibold block mt-1")}>
                                {__("Note: Your website must be publicly accessible for the AI to read files.", "antimanual")}
                            </span>
                        )}
                    </>
                }
                handleFileChange={handleFileChange}
            />

            <div className={css("flex justify-center")}>
                <Button loading={loading} className={css("mt-5 p-3 min-w-sm")} onClick={handleSubmit}>
                    {__("Generate Outline", "antimanual")}
                </Button>
            </div>

            {error && <div className={css("mt-3 notice notice-error")}>{error}</div>}
        </Section>
    );
}
